<script>
  import { createEventDispatcher } from "svelte";
  import {
    Checkbox,
    Modal as DangerModal,
  } from "carbon-components-svelte";
  import { fetchWithSession } from "@utils/fetchWithSession.js";
  import Modal from "../../../../lib/components/Modal.svelte";
  import { isValidRegex } from "@utils/regex.js";
  import { fly } from "svelte/transition";
  import { InfoBlock, Tooltip, TextInput, TextArea } from "paxray-components-svelte";

  export let open = false;
  export let currentRule = {};
  export let idproject = "";
  export let openDangerModal = false;
  export let idToDelete = null;
  export let prefillNumbersToCheck = "";
  export let prefillRequestId = 0;

  const dispatch = createEventDispatcher();

  let numbersToCheck = "";
  let numberChecking = [];
  let lastPrefillRequestId = 0;

  $: if (currentRule) checkNumbers();

  $: if (!open) {
    numbersToCheck = "";
    numberChecking = [];
  }

  $: if (open && prefillRequestId !== lastPrefillRequestId) {
    numbersToCheck = prefillNumbersToCheck || "";
    checkNumbers();
    lastPrefillRequestId = prefillRequestId;
  }

  $: isFormInvalid =
    !String(currentRule.record_type ?? "").trim() ||
    !String(currentRule.prefix ?? "").trim() ||
    currentRule.length == null ||
    currentRule.length === "" ||
    Number.isNaN(Number(currentRule.length)) ||
    !Number.isInteger(Number(currentRule.length)) ||
    Number(currentRule.length) < 1;

  function orderTextInput(e) {
    if (numbersToCheck) {
      const numbers = numbersToCheck.split(/[\n ]+/);
      numbersToCheck = "";
      numbers.forEach((number) => {
        if (number !== "") {
          if (numbersToCheck) numbersToCheck += `\n`;
          numbersToCheck += number;
        }
      });
    }
    if (e.key === "Enter" || e.key === " ") {
      numbersToCheck += `\n`;
    }
    autoResizeTextArea(e);
    checkNumbers();
  }

  function checkNumbers() {
    numberChecking = [];
    if (numbersToCheck) {
      const numbers = numbersToCheck.split(/[\n ]+/);
      numbers.forEach((number) => {
        if (number !== "") {
          numberChecking.push(
            isValidRegex(number) &&
              checkCorrectLength(number) &&
              checkCorrectPrefix(number),
          );
        }
      });
    }
  }

  function checkCorrectLength(number) {
    let inputStr = String(number);
    if (currentRule.padding) {
      inputStr = inputStr.replace(/^0+/, "") || "0";
    }
    return inputStr.length === Number(currentRule.length);
  }

  function checkCorrectPrefix(number) {
    let inputStr = String(number);
    const prefixStr = String(currentRule.prefix);

    if (currentRule.padding) {
      inputStr = inputStr.replace(/^0+/, "") || "0";
    }

    if (inputStr.startsWith(prefixStr)) return true;
    return false;
  }

  function autoResizeTextArea(e) {
    const ta = e.target;
    ta.style.height = "auto";
    ta.style.height = ta.scrollHeight + "px";
  }

  function saveData() {
    const payload = {
      record_type: currentRule.record_type,
      prefix: currentRule.prefix,
      length: Number(currentRule.length),
      padding: currentRule.padding || false,
      track: currentRule.track || false,
    };
    if (currentRule.id) {
      updateData(currentRule.id, payload);
    } else {
      createData(payload);
    }
  }

  function updateData(id, payload) {
    fetchWithSession(`/api/damprojects/number-ranges/${id}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    })
      .then((res) => res.json())
      .then((data) => {
        open = false;
        if (data.ok) dispatch("saved", { data: data.data, isNew: false });
      })
      .catch((err) => {
        console.error("Error updating data:", err);
      });
  }

  function createData(payload) {
    payload.project_id = idproject;
    fetchWithSession(`/api/damprojects/number-ranges`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    })
      .then((res) => res.json())
      .then((data) => {
        open = false;
        if (data.ok) dispatch("saved", { data: data.data, isNew: true });
      })
      .catch((err) => {
        console.error("Error creating data:", err);
      });
  }

  function deleteData(id) {
    fetchWithSession(`/api/damprojects/number-ranges/${id}`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then((res) => res.json())
      .then((data) => {
        openDangerModal = false;
        if (data.ok) dispatch("deleted", { id: idToDelete });
      })
      .catch((err) => {
        console.error("Error deleting data:", err);
      });
  }

  $$restProps;
</script>


<Modal
  preventCloseOnClickOutside
  actions
  autowidth
  bind:open
  modalHeading={currentRule.id ? "Edit Number Range" : "New Number Range"}
  subtitle="Define how task registration values should be recognized as business object IDs."
  primaryButtonText="Save"
  primaryButtonValidations={isFormInvalid}
  primaryButtonNotAllow={isFormInvalid}
  secondaryButtonText="Cancel"
  on:open
  primaryButtonAction={saveData}
  on:click:button--secondary={() => (open = false)}
>
  <div class="modal-content">
    <section class="form-section">
      <InfoBlock
        heading="Number Range"
        description="Set the name and format used to recognize matching values."
        headingLevel={6}
      />
      <div class="definition-fields">
        <div class="field-group record-type-field">
          <TextInput
            hideLabel
            light
            labelText="Record Type"
            required
            tooltipText="Business object or ID type that this number range represents, for example Order, Ticket, or Invoice."
            placeholder="e.g. Invoice"
            bind:value={currentRule.record_type}
          />
        </div>
        <div class="field-group prefix-field">
          <TextInput
            hideLabel
            light
            required
            tooltipText="Static start of the ID. Values must begin with this prefix after optional zero-padding is handled."
            labelText="Prefix"
            placeholder="e.g. 47"
            bind:value={currentRule.prefix}
          />
        </div>
        <div class="field-group length-field">
          <TextInput
            hideLabel
            light
            required
            tooltipText="Expected total length of the value. Enter a positive number."
            tooltipDirection="bottom"
            labelText="Length"
            placeholder="e.g. 10"
            bind:value={currentRule.length}
            on:input={checkNumbers}
          />
        </div>
      </div>
    </section>

    <section class="form-section">
      <InfoBlock
        heading="Behavior"
        description="Choose how matching and downstream analysis should handle this range."
        headingLevel={6}
      />
      <div class="option-fields">
        <div class="checkbox-form">
          <Checkbox
            labelText="Ignore leading zeros"
            bind:checked={currentRule.padding}
          />
          <div>
            <Tooltip
              text="Leading zeros are removed before prefix and length are checked."
            />
          </div>
        </div>
        <div class="checkbox-form">
          <Checkbox
            id="number-range-track"
            labelText="Track matching objects"
            bind:checked={currentRule.track}
          />
          <div>
            <Tooltip
              text="Use this number range as a tracked business object in downstream analysis."
            />
          </div>
        </div>
      </div>
    </section>

    <section class="form-section check-section">
      <InfoBlock
        heading="Test Numbers"
        description="Optionally paste one or more sample values to verify the configuration."
        headingLevel={6}
      />
      <div class="check-numbers">
        <div class="checked">
          {#each numberChecking as check}
            <p
              class={check ? "valid" : "invalid"}
              title={check
                ? "Matches this number range"
                : "Does not match this number range"}
            >
              {check ? "✓" : "X"}
            </p>
          {/each}
        </div>

        <TextArea
          hideLabel
          light
          labelText="Test Numbers"
          placeholder="Enter one number per line"
          bind:value={numbersToCheck}
          on:keyup={(e) => {
            orderTextInput(e);
          }}
        />
      </div>
    </section>
  </div>
</Modal>

<DangerModal
  preventCloseOnClickOutside
  danger
  size="sm"
  bind:open={openDangerModal}
  modalHeading={`Delete number range`}
  primaryButtonText="Delete"
  secondaryButtonText="Cancel"
  on:click:button--primary={() => {
    deleteData(idToDelete);
    openDangerModal = false;
  }}
  on:click:button--secondary={() => (openDangerModal = false)}
>
  <div class="deleteModalBody">
    <p>Are you sure you want to delete this number range?</p>
    <span>This action cannot be undone.</span>
  </div>
</DangerModal>

<style>
  .modal-content {
    display: flex;
    flex-direction: column;
    gap: var(--control-gap);
  }

  .form-section {
    display: flex;
    flex-direction: column;
    gap: var(--control-gap);
  }

  .form-section + .form-section {
    padding-top: 1rem;
  }

  .definition-fields {
    display: grid;
    grid-template-columns: minmax(12rem, 1fr) minmax(10rem, 1fr) 8rem;
    gap: var(--control-gap);
  }

  .field-group {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    min-width: 0;
  }

  .checkbox-form {
    display: flex;
    align-items: center;
    gap: 0.35rem;
  }

  .option-fields {
    display: grid;
    grid-template-columns: repeat(2, minmax(12rem, 1fr));
    gap: var(--control-gap);
  }

  .checked {
    color: var(--pax-white);
    width: 1lh;
    height: 1lh;
    padding-top: 2.25rem;
    text-align: center;
  }

  .check-section {
    padding-bottom: 0.25rem;
  }

  .check-numbers {
    display: flex;
    flex-direction: row;
    min-height: 6rem;
    max-height: 10rem;
    overflow: hidden;
    gap: 0rem;
    overflow-y: auto;
    width: 100%;
  }

  .checked p {
    font-size: 0.8%;
    padding: 0;
    border-radius: 0.5rem;
    outline: 2px solid var(--pax-white);
    outline-offset: -1.5px;
  }
  .checked .valid {
    background-color: var(--pax-green);
  }

  .checked .invalid {
    background-color: var(--pax-danger);
  }

  :global(.checkbox-form .bx--checkbox-wrapper) {
    flex: 0 0 auto;
    width: auto;
    margin-top: 0 !important;
  }

  :global(.definition-fields .bx--text-input),
  :global(.check-numbers .bx--text-area) {
    border: solid 1px var(--pax-black-inactive);
    border-radius: 0.5rem;
  }

  :global(.definition-fields .bx--text-input) {
    height: 48px;
  }

  :global(.definition-fields .bx--text-input::placeholder),
  :global(.check-numbers .bx--text-area::placeholder) {
    color: var(--pax-black-inactive);
  }

  :global(.definition-fields .bx--text-input:focus),
  :global(.check-numbers .bx--text-area:focus) {
    border-color: transparent;
    outline: solid var(--bw) var(--pax-black);
    color: var(--pax-black);
  }

  :global(.check-numbers .bx--text-area__wrapper) {
    width: 100%;
    min-width: 0;
  }
  :global(
      .check-numbers .bx--form-item .bx--text-area__wrapper .bx--text-area
    ) {
    height: auto;
    min-height: 6rem;
    max-height: 10rem;
    width: 100%;
    min-width: 0;
    resize: none;
    overflow-y: hidden;
  }

  :global(
      .check-numbers
        .bx--form-item
        .bx--text-area__wrapper
        .bx--text-area:active
    ),
  :global(
      .check-numbers .bx--form-item .bx--text-area__wrapper .bx--text-area:focus
    ) {
    outline: none;
  }

  @media (max-width: 760px) {
    .definition-fields,
    .option-fields {
      grid-template-columns: 1fr;
    }
  }
</style>
